import React from 'react';
import { View } from 'react-native';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

export interface ProgressBarProps {
  /** Completed share, 0 to 1; anything outside is clamped. */
  value: number;
  /**
   * `onInverse` for a bar drawn on an inverse (dark-on-light) strip such as the
   * offline banner's slot.
   * @default "default"
   */
  tone?: 'default' | 'onInverse';
  /** Spoken name of what is progressing, e.g. "Syncing 3 of 7". */
  accessibilityLabel?: string;
  testID?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  tone = 'default',
  accessibilityLabel,
  testID,
}) => {
  const share = Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 1);
  const inverse = tone === 'onInverse';
  return (
    <View
      style={[styles.track, inverse ? styles.trackInverse : null]}
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={accessibilityLabel}
      accessibilityValue={{ min: 0, max: 100, now: Math.round(share * 100) }}
      testID={testID}
    >
      <View
        style={[
          styles.fill,
          inverse ? styles.fillInverse : null,
          { width: `${share * 100}%` },
        ]}
        testID={testID ? `${testID}-fill` : undefined}
      />
    </View>
  );
};

ProgressBar.displayName = 'ProgressBar';

const styles = themedStyleSheet((role) => ({
  track: {
    height: 4,
    borderRadius: 2,
    backgroundColor: role.borderSubtle,
    overflow: 'hidden',
  },
  trackInverse: {
    backgroundColor: role.inverseDim,
  },
  fill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: role.textStrong,
  },
  fillInverse: {
    backgroundColor: role.textOnInverse,
  },
}));
